import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import CaseManagementForm from "./CaseManagementForm";
import { createCaseManagement } from "../../store/slices/caseManagementSlice";

const AddCaseManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { patientId } = state || {};
  const { isLoading } = useSelector((state) => state.caseManagements);

  const createDefaultRecord = () => ({
    patient_id: patientId || "",
    patient_id_fixed: !!patientId,
    case_manager: "",
    call_type: "",
    interaction_type: "",
    calling_date: new Date().toISOString().split("T")[0],
    remarks: "",
    occupational_status: [],
    occupational_status_other: "",
    review_frequency: "",
    service_type: "",
    service_schedule_date: "",
    service_frequency: "",
  });

  const [records, setRecords] = useState([createDefaultRecord()]);
  const [expandedIndices, setExpandedIndices] = useState([0]);
  const [errors, setErrors] = useState({});

  const handleAddRow = () => {
    const nextIndex = records.length;
    setExpandedIndices([nextIndex]);
    setRecords((prev) => [...prev, createDefaultRecord()]);
  };

  const handleRemoveRow = (index) => {
    setRecords((prev) => prev.filter((_, i) => i !== index));
    // Shift expanded indices down
    setExpandedIndices((prev) =>
      prev
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i))
    );
    // Shift errors down
    setErrors((prev) => {
      const updated = {};
      Object.keys(prev).forEach((k) => {
        const ik = parseInt(k);
        if (ik < index) {
          updated[ik] = prev[ik];
        } else if (ik > index) {
          updated[ik - 1] = prev[ik];
        }
      });
      return updated;
    });
  };

  const toggleRow = (index) => {
    setExpandedIndices((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const handleFormChange = (index, updatedFormData) => {
    setRecords((prev) => {
      const updated = [...prev];
      updated[index] = updatedFormData;
      return updated;
    });
  };

  const validateRow = (record) => {
    const rowErrors = {};
    if (!record.patient_id) rowErrors.patient_id = ["Patient is required"];
    return rowErrors;
  };

  const validateAll = () => {
    const newErrors = {};
    let isValid = true;

    records.forEach((record, index) => {
      const rowErrors = validateRow(record);
      if (Object.keys(rowErrors).length > 0) {
        newErrors[index] = rowErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);

    if (!isValid) {
      const errorIndices = Object.keys(newErrors).map(Number);
      setExpandedIndices(errorIndices);
    }

    return isValid;
  };

  // SAVE INDIVIDUAL ROW
  const handleSaveIndividual = async (index) => {
    const record = records[index];
    const rowErrors = validateRow(record);

    if (Object.keys(rowErrors).length > 0) {
      setErrors((prev) => ({ ...prev, [index]: rowErrors }));
      Swal.fire({
        title: "Validation Error",
        text: "Please select a patient for this record.",
        icon: "warning",
      });
      return;
    }

    // Clear error for this index
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });

    try {
      await dispatch(createCaseManagement(record)).unwrap();

      Swal.fire({
        title: "Saved!",
        text: "Record has been saved successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      // If it was the last/only row, navigate back
      if (records.length === 1) {
        navigate("/case-management");
      } else {
        // Otherwise remove just this row from the repeater list
        handleRemoveRow(index);
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: typeof err === "object" ? Object.values(err).flat().join(" ") : (err || "Failed to save record."),
        icon: "error",
      });
    }
  };

  // SAVE ALL ROWS BULK
  const handleSaveAll = async (e) => {
    e.preventDefault();

    if (!validateAll()) {
      Swal.fire({
        title: "Validation Error",
        text: "Please correct the errors in the highlighted records.",
        icon: "warning",
      });
      return;
    }

    try {
      const payload = {
        records: records.map((r) => ({
          patient_id: r.patient_id,
          case_manager: r.case_manager,
          call_type: r.call_type,
          interaction_type: r.interaction_type,
          calling_date: r.calling_date,
          remarks: r.remarks,
          occupational_status: r.occupational_status,
          occupational_status_other: r.occupational_status_other,
          review_frequency: r.review_frequency,
          service_type: r.service_type,
          service_schedule_date: r.service_schedule_date,
          service_frequency: r.service_frequency,
        })),
      };

      await dispatch(createCaseManagement(payload)).unwrap();

      Swal.fire({
        title: "Success!",
        text: `${records.length} Record(s) added successfully.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      navigate("/case-management");
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: typeof err === "object" ? Object.values(err).flat().join(" ") : (err || "Failed to save records."),
        icon: "error",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />

        <form onSubmit={handleSaveAll}>
          {records.map((record, index) => {
            const isExpanded = expandedIndices.includes(index);
            const rowErrors = errors[index] || {};

            return (
              <div className="card mb-4 border border-1 border-primary-subtle" key={index}>
                <div
                  className="card-header d-flex justify-content-between align-items-center bg-light py-2 px-4"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => toggleRow(index)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className={`ri-arrow-${isExpanded ? "down" : "right"}-s-line text-secondary ri-lg`}></i>
                    <h6 className="mb-0 text-primary fw-bold">Case Management Record #{index + 1}</h6>
                    {!isExpanded && (
                      <span className="badge bg-label-secondary ms-2 small">
                        (Click to expand / edit)
                      </span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      onClick={() => handleSaveIndividual(index)}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line me-1"></i> Save Individual
                        </>
                      )}
                    </button>
                    {records.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveRow(index)}
                      >
                        <i className="ri-delete-bin-line me-1"></i> Remove Row
                      </button>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div className="card-body p-0 border-top">
                    <CaseManagementForm
                      formData={record}
                      setFormData={(updater) => {
                        let nextData;
                        if (typeof updater === "function") {
                          nextData = updater(record);
                        } else {
                          nextData = updater;
                        }
                        handleFormChange(index, nextData);
                      }}
                      errors={rowErrors}
                    />
                  </div>
                )}
              </div>
            );
          })}

          <div className="row mt-3 mb-5">
            <div className="col-md-3">
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                onClick={handleAddRow}
              >
                <i className="ri-add-circle-line me-1"></i> Add Another Row
              </button>
            </div>
            <div className="col-md-2 ms-auto">
              <button type="submit" className="btn btn-primary w-100 sticky-form-submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-3-line me-1"></i> Save All
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddCaseManagement;
